import { Link } from "react-router-dom";
import { AdvancedJson, KeyVal, formatAge } from "./phase3";
import { StatusIndicator } from "./ui";
import type {
  BalderAllowance,
  BalderConfirmation,
  BalderToolActivity,
  BlueprintDraftChangeResult,
  DeploymentReviewResult,
  DryRunToolResult,
  GuildInspectionResult,
} from "../lib/api";

/**
 * Presentational primitives for the BALDER workspace. No data fetching happens
 * here so every card can be unit-tested with mock data.
 *
 * SAFETY POSTURE encoded here:
 *  - BALDER is advisory. Nothing in these cards deploys, approves, or mutates a
 *    live Discord server. Blueprint edits are DRAFTS only.
 *  - Tool results are rendered as safe, structured fields — never raw Discord
 *    payloads. The optional "advanced" disclosure shows the already-redacted
 *    structured result, nothing else.
 *  - The exact safety phrasings required by the product spec live as constants so
 *    they cannot be silently weakened and tests can assert on them verbatim.
 */

// ── Exact safety phrasings (do not weaken) ────────────────────────────────────
export const DRAFT_PREPARED_TEXT = "BALDER has prepared a draft.";
export const NO_DISCORD_CHANGES_TEXT = "No Discord changes have been made.";
export const DRY_RUN_PREVIEW_TEXT = "This dry run is a preview.";
export const DRY_RUN_NO_CHANGES_TEXT = "No Discord changes were made.";
export const HUMAN_APPROVAL_TEXT = "A human approval is required before deployment.";
export const BALDER_CANNOT_DEPLOY_TEXT = "BALDER cannot approve or execute deployments.";
export const PLAN_STALE_TEXT = "The current plan is stale and must be regenerated.";
export const CONFIRM_NO_DISCORD_TEXT = "Discord will not be changed.";

// ── Secret guard ──────────────────────────────────────────────────────────────
/**
 * Best-effort client-side check for token-like content. The server is the
 * authority and also blocks secrets — this only spares the user from ever
 * transmitting one by accident. It intentionally errs toward false positives.
 */
export function looksLikeSecret(text: string): boolean {
  if (!text) return false;
  // Discord bot token: <base64 id>.<base64 ts>.<base64 hmac>
  const discordToken = /[A-Za-z\d_-]{23,28}\.[A-Za-z\d_-]{6,7}\.[A-Za-z\d_-]{27,}/;
  // "Bot <token>" authorization form.
  const botAuth = /\bBot\s+[A-Za-z\d_.-]{24,}/;
  // Generic long opaque secret (bearer tokens, client secrets, etc.).
  const genericLong = /(?:^|[^A-Za-z0-9_-])[A-Za-z0-9_-]{48,}(?:$|[^A-Za-z0-9_-])/;
  return discordToken.test(text) || botAuth.test(text) || genericLong.test(text);
}

// ── Tool activity card ────────────────────────────────────────────────────────
const TOOL_TITLE: Record<string, string> = {
  "guild-inspection": "Guild inspection",
  "blueprint-draft-change": "Blueprint draft change",
  "dry-run": "Dry-run preview",
  "deployment-review": "Deployment review",
};

function phaseTone(phase: BalderToolActivity["phase"]) {
  switch (phase) {
    case "completed":
      return "ok" as const;
    case "failed":
      return "danger" as const;
    case "started":
      return "warn" as const;
    default:
      return "neutral" as const;
  }
}

export function ToolActivityCard({ tool }: { tool: BalderToolActivity }): JSX.Element {
  const title = tool.title ?? TOOL_TITLE[tool.kind] ?? "BALDER tool";
  const running = tool.phase === "proposed" || tool.phase === "started";
  return (
    <div className={`tool-card tool-card--${tool.kind}`} data-tool={tool.kind}>
      <div className="tool-card__head">
        <span className="tool-card__icon" aria-hidden="true" />
        <strong className="tool-card__title">{title}</strong>
        <StatusIndicator tone={phaseTone(tool.phase)} label={tool.phase} />
      </div>
      {tool.summary ? <p className="tool-card__summary">{tool.summary}</p> : null}

      {tool.phase === "failed" ? (
        <p className="tool-card__error">{tool.error ?? "This tool did not complete."}</p>
      ) : running ? (
        <p className="muted">Working… no changes are being made to Discord.</p>
      ) : (
        <ToolBody tool={tool} />
      )}

      {tool.result ? (
        <AdvancedJson title="Advanced — redacted structured result" value={tool.result} />
      ) : null}
    </div>
  );
}

function ToolBody({ tool }: { tool: BalderToolActivity }): JSX.Element | null {
  switch (tool.kind) {
    case "guild-inspection":
      return <GuildInspectionBody r={(tool.result ?? {}) as GuildInspectionResult} />;
    case "blueprint-draft-change":
      return <BlueprintDraftBody r={(tool.result ?? {}) as BlueprintDraftChangeResult} />;
    case "dry-run":
      return <DryRunBody r={(tool.result ?? {}) as DryRunToolResult} />;
    case "deployment-review":
      return <DeploymentReviewBody r={(tool.result ?? {}) as DeploymentReviewResult} />;
    default:
      return tool.result ? null : <p className="muted">Completed.</p>;
  }
}

function GuildInspectionBody({ r }: { r: GuildInspectionResult }): JSX.Element {
  const counts = r.resourceCounts ?? {};
  const freshLabel =
    r.snapshotFresh === undefined
      ? "unknown"
      : r.snapshotFresh
        ? "fresh"
        : "stale — consider a new snapshot";
  return (
    <div className="kv-grid">
      <KeyVal k="Server" v={r.guildName ?? "—"} />
      <KeyVal k="Bot status" v={r.botStatus ?? "unknown"} />
      <KeyVal
        k="Snapshot"
        v={<StatusIndicator tone={r.snapshotFresh ? "ok" : "warn"} label={freshLabel} />}
      />
      <KeyVal
        k="Snapshot age"
        v={
          typeof r.snapshotAgeSeconds === "number"
            ? formatAge(new Date(Date.now() - r.snapshotAgeSeconds * 1000).toISOString())
            : "—"
        }
      />
      <KeyVal k="Roles" v={counts.roles ?? "—"} />
      <KeyVal k="Categories" v={counts.categories ?? "—"} />
      <KeyVal k="Channels" v={counts.channels ?? "—"} />
      <KeyVal k="Permission overwrites" v={counts.overwrites ?? "—"} />
    </div>
  );
}

function BlueprintDraftBody({ r }: { r: BlueprintDraftChangeResult }): JSX.Element {
  const resources = r.proposedResources ?? [];
  const v = r.validation;
  return (
    <div className="tool-card__section">
      <p className="balder-draft-banner">
        <strong>{DRAFT_PREPARED_TEXT}</strong> {NO_DISCORD_CHANGES_TEXT}
      </p>
      <div className="kv-grid">
        <KeyVal k="Blueprint" v={r.blueprintName ?? r.blueprintId ?? "—"} />
        <KeyVal
          k="Validation"
          v={
            v ? (
              <StatusIndicator
                tone={v.valid ? "ok" : "danger"}
                label={v.valid ? "valid" : `invalid (${v.errors ?? 0} errors)`}
              />
            ) : (
              "—"
            )
          }
        />
        {v && (v.warnings ?? 0) > 0 ? <KeyVal k="Warnings" v={v.warnings} /> : null}
      </div>
      {resources.length > 0 ? (
        <ul className="balder-resource-list">
          {resources.map((res, i) => (
            <li key={`${res.type}-${res.name}-${i}`}>
              <span className={`marker marker--${res.action === "remove" ? "destructive" : "protected"}`}>
                {res.action}
              </span>{" "}
              <span className="muted">{res.type}</span> {res.name}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No resource-level changes were listed.</p>
      )}
      <p className="section-note">
        This is a change to a blueprint draft in ODIN only.{" "}
        {r.undoAvailable ? "This draft change can be undone." : "Publishing a draft is a separate, human-controlled step."}
      </p>
    </div>
  );
}

function DryRunBody({ r }: { r: DryRunToolResult }): JSX.Element {
  const stale = r.planStatus === "STALE";
  return (
    <div className="tool-card__section">
      <p className="balder-draft-banner">
        <strong>{DRY_RUN_PREVIEW_TEXT}</strong> {DRY_RUN_NO_CHANGES_TEXT}
      </p>
      <div className="kv-grid">
        <KeyVal
          k="Plan status"
          v={<StatusIndicator tone={stale ? "warn" : r.planStatus === "READY" ? "ok" : "neutral"} label={String(r.planStatus ?? "—")} />}
        />
        <KeyVal k="Operations" v={r.operationCount ?? "—"} />
        <KeyVal k="Warnings" v={r.warnings ?? 0} />
        <KeyVal k="Blockers" v={r.blockers ?? 0} />
      </div>
      {stale ? <p className="tool-card__error">{PLAN_STALE_TEXT}</p> : null}
    </div>
  );
}

function DeploymentReviewBody({ r }: { r: DeploymentReviewResult }): JSX.Element {
  return (
    <div className="tool-card__section">
      <div className="kv-grid">
        <KeyVal k="Plan hash" v={r.planHash ?? "—"} mono />
        <KeyVal k="Approval status" v={String(r.approvalStatus ?? "none")} />
        <KeyVal
          k="Production lock"
          v={<StatusIndicator tone={r.productionLocked ? "ok" : "warn"} label={r.productionLocked ? "Locked (safe)" : "Unlocked"} />}
        />
      </div>
      <p className="section-note">
        {HUMAN_APPROVAL_TEXT} {BALDER_CANNOT_DEPLOY_TEXT}
      </p>
      <p>
        <Link to="/app/release-review">Open the release review page</Link> to review and approve a deployment. Approval
        is always performed by a human there — never here.
      </p>
    </div>
  );
}

// ── Confirmation card (an ODIN-side action requiring explicit sign-off) ────────
export function ConfirmationCard({
  confirmation,
  busy = false,
  onConfirm,
  onReject,
}: {
  confirmation: BalderConfirmation;
  busy?: boolean;
  onConfirm?: (actionId: string) => void;
  onReject?: (actionId: string) => void;
}): JSX.Element {
  const sideEffects = confirmation.sideEffects ?? [];
  return (
    <div className="balder-confirm" role="group" aria-label="BALDER action confirmation">
      <div className="balder-confirm__head">
        <span className="badge">Confirmation required</span>
        <strong>{confirmation.title ?? confirmation.actionType}</strong>
      </div>
      {confirmation.summary ? <p className="balder-confirm__summary">{confirmation.summary}</p> : null}

      <div className="kv-grid">
        <KeyVal k="Intended action" v={confirmation.actionType} />
        {confirmation.tenantId ? <KeyVal k="Workspace" v={confirmation.tenantId} mono /> : null}
        <KeyVal k="Server (guild)" v={confirmation.guildId ?? "—"} mono />
        <KeyVal k="Blueprint" v={confirmation.blueprintName ?? confirmation.blueprintId ?? "—"} />
      </div>

      <div className="balder-confirm__effects">
        <span className="field__label">Expected ODIN-side effects</span>
        {sideEffects.length > 0 ? (
          <ul>
            {sideEffects.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        ) : (
          <p className="muted">This updates ODIN records only.</p>
        )}
      </div>

      {/* The single most important line on this card. */}
      <p className="balder-confirm__safe" role="note">
        <strong>{CONFIRM_NO_DISCORD_TEXT}</strong> This confirms an action inside ODIN only.
      </p>

      <div className="row">
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy}
          onClick={() => onConfirm?.(confirmation.actionId)}
        >
          {busy ? "Working…" : "Confirm this action"}
        </button>
        <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => onReject?.(confirmation.actionId)}>
          Reject
        </button>
      </div>
    </div>
  );
}

// ── AI usage / allowance strip ────────────────────────────────────────────────
/**
 * Spec 2.3: "Do not hide usage information until the allowance is exhausted."
 *
 * The failure this prevents is a customer discovering their limit by being
 * refused mid-task. So the strip is always on screen, always shows the plan
 * allowance and what is left of it, and states the two rules people otherwise
 * only learn the hard way: nothing is charged when the limit is hit, and unused
 * requests do not carry over.
 */
export const BALDER_NO_OVERAGE_TEXT =
  "Paid overage is off. BALDER stops when the allowance runs out — nothing is charged automatically.";
export const BALDER_EXPIRY_TEXT =
  "Included and purchased requests both reset at the start of each billing period. Unused requests do not roll over.";
export const BALDER_UNMETERED_TEXT = "This workspace has no BALDER request limit.";

/** Format a reset instant as a plain UTC date (YYYY-MM-DD), or null when unknown. */
export function formatResetDate(epochSec: number | null | undefined): string | null {
  if (typeof epochSec !== "number" || !Number.isFinite(epochSec)) return null;
  const d = new Date(epochSec * 1000);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

export function AllowanceStrip({
  allowance,
  loading = false,
  error = null,
}: {
  allowance: BalderAllowance | null;
  loading?: boolean;
  error?: string | null;
}): JSX.Element {
  if (loading) {
    return (
      <div className="balder-allowance" aria-label="BALDER usage">
        <span className="muted">Checking your BALDER allowance…</span>
      </div>
    );
  }

  // An unreadable meter is reported as unreadable. Rendering a zero here would
  // tell a paying customer their allowance was gone.
  if (error) {
    return (
      <div className="balder-allowance balder-allowance--unknown" aria-label="BALDER usage">
        <span className="muted">
          Your BALDER allowance could not be read just now, so the numbers below are unavailable. BALDER still works.
        </span>
      </div>
    );
  }

  if (!allowance || allowance.allowance === null) {
    return (
      <div className="balder-allowance" aria-label="BALDER usage">
        <span className="badge">BALDER usage</span>
        <span className="muted">{BALDER_UNMETERED_TEXT}</span>
      </div>
    );
  }

  const reset = formatResetDate(allowance.resetEpochSec);
  const remaining = allowance.remaining ?? 0;
  const exhausted = remaining <= 0;
  const low = !exhausted && allowance.allowance > 0 && remaining / allowance.allowance <= 0.15;
  const tone = exhausted ? "balder-allowance--out" : low ? "balder-allowance--low" : "";

  return (
    <div className={`balder-allowance ${tone}`.trim()} aria-label="BALDER usage">
      <div className="balder-allowance__head">
        <span className="badge">BALDER usage</span>
        {allowance.planName ? <span className="muted">{allowance.planName} plan</span> : null}
      </div>

      <div className="balder-allowance__figures">
        <KeyVal k="Included this period" v={`${allowance.included ?? 0} requests`} />
        <KeyVal k="Purchased extra" v={`${allowance.purchased} requests`} />
        <KeyVal k="Used" v={`${allowance.consumed} of ${allowance.allowance}`} />
        <KeyVal k="Remaining" v={`${remaining} requests`} />
        <KeyVal k="Resets" v={reset ?? "at the start of the next billing period"} />
      </div>

      <p className="muted balder-allowance__rules">
        {BALDER_EXPIRY_TEXT} {allowance.overageBehavior === "deny" ? BALDER_NO_OVERAGE_TEXT : null}
      </p>

      {exhausted ? (
        <p className="balder-allowance__out" role="status">
          You have used the whole BALDER allowance for this period. Every other part of ODIN keeps working — this limit
          only applies to BALDER. <Link to="/app/billing">Change plan or buy more BALDER usage</Link> if you need more
          before {reset ?? "the next period"}.
        </p>
      ) : null}
    </div>
  );
}
